import { Component, OnInit, OnDestroy, OnChanges, Input, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import * as words from '../../../../services/language'
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import { Subject, Subscription } from "rxjs";
import { SEARCH_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { Receipt } from 'src/app/models/receipt.model';
import { receiptIcon } from 'src/app/utils/icons';

@Component({
    selector: 'receipts',
    templateUrl: './receipts.component.html',
    styleUrls: ['./receipts.component.scss'],
    standalone: false
})
export class ReceiptsComponent implements OnInit, OnChanges, OnDestroy {

    // Unique internal identity of the lot whose recent receipts will be queried for.
    @Input() lotId: number = 0

    // Unique internal identity of the contact whose recent receipts will be queried for, overrides the lotId.
    @Input() contactId: number = 0

    // Unique internal identity of the sale whose recent receipts will be queried for, overrides the lotId and contactId.
    @Input() saleId: number = 0

    // Unique internal identity of the sale finance type to filter the recent receipts with.
    @Input() financeTypeId: number = 0

    // An additional key to use to identify this component when multiple could be on the same page.
    @Input() key: string = ''

    // A payment was taken so lets update our available receipts and any suppporting displays.
    @Input() paymentTaken: Subject<Object>

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    // Communicate refund updates to the parent component.
    @Output() refundProcessed: EventEmitter<number> = new EventEmitter()

    //
    protected receipts: Array<Receipt> = []

    //
    private records: number = 0

    //
    private page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    // A handle to a payment taken subscription so we can unsubscribe later.
    private paymentTakenWatcher: Subscription | null = null

    // The receipt number search form group
    protected form: FormGroup | null = null

    // A receipt number to filter the list by.
    protected receiptNumber = null

    /** A debounce timer for search events. Used to wait for the user to stop typing before we fetch. */
    private watcher: ReturnType<typeof setTimeout> | null = null

    /**  */
    private inquiryWatcher: Subscription | null = null

    private subscriptions: Subscription[] = []

    // Unique internal identity of the receipt the user wants to view.
    protected receiptId: number = 0

    constructor (private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, private store: StoreService, private fb: FormBuilder) {
        this.form = this.fb.group({
            number: new FormControl<string>('', [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('number').valueChanges.subscribe(number => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (this.receiptNumber === number) {
                    return
                }

                if (!/^[0-9]*$/g.test(number)) {
                    return
                }
        
                this.watcher = setTimeout(() => {
                    this.receiptNumber = number
                    this.reset()            
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        if (this.paymentTaken) {
            this.subscriptions.push(
                this.paymentTaken.subscribe((payment) => {
                    this.reset()
                    this.fetch()
                })
            )
        }

        this.reset()
        this.fetch()
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.lotId || changes.contactId || changes.saleId || changes.financeTypeId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    private fetch = (): void => {
        if (!this.lotId && !this.contactId && !this.saleId) {
            return
        }

        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        let lotParam = ''
        if (this.lotId && (this.lotId > 0)) {
            lotParam = `&lot=${this.lotId}`
        }

        let contactParam = ''
        if (this.contactId && (this.contactId > 0)) {
            contactParam = `&contact=${this.contactId}`
            lotParam = ''
        }

        let saleParam = ''
        if (this.saleId && (this.saleId > 0)) {
            saleParam = `&sale=${this.saleId}`
            lotParam = ''
            contactParam = ''
        }

        let financeTypeParam = ''
        if (this.financeTypeId && (this.financeTypeId > 0)) {
            financeTypeParam = `&financeType=${this.financeTypeId}`
        }

        let rcptParam = ''
        if (this.receiptNumber && (this.receiptNumber.length > 0)) {
            rcptParam = `&number=${this.receiptNumber}`
        }

        let spanParam = '&span=quarter'
        if (contactParam && (contactParam.length > 0)) {
            spanParam = '&ipp=9'
        }

        this.loading = true
        if (this.page === 1) {
            this.receipts = []
        }

        this.master.get(`receipts?page=${this.page}&sort=posted|DESC${lotParam}${contactParam}${saleParam}${financeTypeParam}${rcptParam}${spanParam}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = res.data.records
            this.exhausted = (res.data.next === null)
  
            if (this.page > 1) {
                this.receipts = this.receipts.concat(res.data.receipts)
                return
            }
  
            this.receipts = res.data.receipts
        })
    }

    protected icon = (category: string) => {
        return receiptIcon(category)
    }

    /**
     * A payment in the list was clicked. 
     * @param e The event meta that trigger the payment selection.
     * @param receipt The payment object that was chosen by the user.
     */
    protected selected = (e: Event, receipt: Object): void => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!receipt || !receipt['id']) {
            return
        }

        this.view(e, receipt)
    }

    /**
     * View a receipt for the selected payment.
     * @param e The event meta that triggered the receipt view
     * @param receipt The payment object that was chosen by the user.
     */
    protected view = (e: Event, receipt: Object): void => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!receipt || !receipt['id']) {
            return
        }

        this.receiptId = parseInt(receipt['id'])
    }

    /**
     * Listen for the user to close the receipt modal
     * @param {boolean} refunded Indicates whether or not the user decided to refund, part or whole, the payment.
     */
    protected receiptClosed = (refunded: boolean) => {
        if (refunded) {
            this.refundProcessed.emit(this.receiptId)

            this.reset()
            this.fetch()
        }

        this.receiptId = 0
        
        const btn = document.getElementById(`toggleReceiptView-${this.key}`) as HTMLButtonElement
        if (!btn) {
            return
        }

        btn.click()
    }
}
